import type {
	CatalogGroup,
	CatalogItemData,
	FiltersSearchParams,
} from 'plp/types/Catalog'
import type {
	ApplyArgs,
	FiltersData,
	RangePickerValue,
	UseFiltersSlotItem,
	UserFilter,
} from 'plp/types/Filters'
import type { Dispatch, SetStateAction } from 'react'

import { EventName } from '../../types/eventsObserver'
import { eventsObserver } from '../../utils/eventsObserver'
import { ALL, ALL_PRICES, ORDER_METHODS, RECOMMENDED } from './constants'
import { filterItems } from './filterItems'
import { updateFilter } from './updateFilter'

export function b64decode(str: string) {
	return Buffer.from(str, 'base64').toString()
}

export function b64encode(str: string) {
	return Buffer.from(str).toString('base64')
}

export function getFilterId(filters: UserFilter) {
	return filters.id ? `${filters.type}-${filters.id}` : filters.type
}

export function cloneFilters(filters: UserFilter[]) {
	return filters.map((filter) => ({ ...filter }))
}

export function catalogItems(
	filters: FiltersData,
	catalogGroups: CatalogGroup[]
): CatalogItemData[] {
	let recommendedIndex = 0
	const products = catalogGroups.flatMap((group) =>
		group.items.map((item) => {
			const index = recommendedIndex
			recommendedIndex += 1
			return { ...item, index }
		})
	)
	return Object.entries(filters.items).flatMap(([slotId, item]) => {
		const found = products.find((product) => product.id === slotId)
		const VALUE_INDEX = 1
		const foundGeneric = filters.generic
			? Object.entries(filters.generic).find(([genericId]) => {
					return genericId === item.productId
				})
			: undefined
		if (!found) {
			return []
		}
		return {
			...item,
			...foundGeneric?.[VALUE_INDEX],
			id: found.id,
			index: found.index >= 0 ? found.index : Number.MAX_SAFE_INTEGER,
		}
	})
}

export function getRangePriceLimits(list: UseFiltersSlotItem[]) {
	return list.reduce(
		(previousRange, item) => {
			const currentRange = previousRange
			if (item.price < currentRange.min) {
				currentRange.min = Math.floor(item.price)
			}
			if (item.price > currentRange.max) {
				currentRange.max = Math.ceil(item.price)
			}
			return currentRange
		},
		{
			min: list?.[0]?.price || 0,
			max: list?.[0]?.price || 0,
		}
	)
}

export function clearSelection(filtersData: UserFilter[]): UserFilter[] {
	return filtersData.map((filter) => {
		const newFilter = filter
		newFilter.selection = filter.multipleChoice ? [] : [ALL]
		return newFilter
	})
}

export function getHasServerFilters(
	defaultFilter: FiltersSearchParams | undefined
): boolean {
	return !!Object.values(defaultFilter ?? {}).flatMap((exist) => exist ?? [])
		.length
}

export function getHasSelection(
	selectedFilters: UserFilter[],
	selectedOrder: string,
	selectedRange?: RangePickerValue,
	selectedLimitRange?: RangePickerValue
) {
	const isDefault = selectedOrder !== RECOMMENDED
	const isUserRangeSelection =
		selectedRange?.min !== selectedLimitRange?.min ||
		selectedRange?.max !== selectedLimitRange?.max
	return (
		!!selectedFilters
			.map((filter) =>
				filter.selection
					.filter((selected) => selected !== ALL)
					.map((option) => option)
			)
			.flat().length ||
		isDefault ||
		isUserRangeSelection
	)
}

export function filteredItemsOnApply(
	originalItems: UseFiltersSlotItem[],
	selectedFilters: UserFilter[],
	selectedOrder: string,
	selectedRange?: RangePickerValue
): UseFiltersSlotItem[] | undefined {
	return filterItems(
		selectedFilters,
		originalItems,
		selectedRange ?? ALL_PRICES,
		ORDER_METHODS[selectedOrder]
	)
}

export function getNewRangeLimit(
	filteredItems: UseFiltersSlotItem[],
	selectedRange?: RangePickerValue,
	selectedLimitRange?: RangePickerValue
): RangePickerValue {
	const newLimitRange = getRangePriceLimits(filteredItems)
	const isOnLimit =
		selectedLimitRange?.max === selectedRange?.max &&
		selectedLimitRange?.min === selectedRange?.min
	let newSelectedRange = selectedRange ?? newLimitRange
	if (!isOnLimit) {
		if (selectedRange && newLimitRange.max < selectedRange.max) {
			newSelectedRange.max = newLimitRange.max
		}
		if (selectedRange && newLimitRange.min > selectedRange.min) {
			newSelectedRange.min = newLimitRange.min
		}
	} else {
		newSelectedRange = newLimitRange
	}
	return newSelectedRange
}

export function clear(
	selectedFilters: UserFilter[],
	selectedLimitRange: RangePickerValue | undefined,
	setSelectedFilters: Dispatch<SetStateAction<UserFilter[]>>,
	setSelectedOrder: Dispatch<SetStateAction<string>>,
	setSelectedRange: Dispatch<SetStateAction<RangePickerValue | undefined>>
) {
	const clearedFilters = clearSelection(selectedFilters)
	setSelectedFilters(clearedFilters)
	setSelectedOrder(RECOMMENDED)
	setSelectedRange(selectedLimitRange)
}

export function toggle(
	type: string,
	value: string,
	id: string,
	selectedFilters: UserFilter[],
	setSelectedFilters: Dispatch<SetStateAction<UserFilter[]>>
) {
	const newSelectedFilters = updateFilter(type, id, value, selectedFilters)
	setSelectedFilters(newSelectedFilters)
}

export function normalizeRange(value?: RangePickerValue) {
	return {
		min: Math.floor(value?.min ?? 0),
		max: Math.ceil(value?.max ?? 0),
	}
}

export function getLimitAndRange(
	filteredItems: UseFiltersSlotItem[],
	selectedRange: RangePickerValue | undefined,
	selectedLimitRange: RangePickerValue | undefined
) {
	const newLimitRange = getRangePriceLimits(filteredItems)
	const newSelectedRange = getNewRangeLimit(
		filteredItems,
		selectedRange,
		selectedLimitRange
	)
	return { limit: newLimitRange, range: newSelectedRange }
}

export function getCurrentSelected(
	args: ApplyArgs,
	selectedFilters: UserFilter[],
	selectedOrder: string,
	selectedRange: RangePickerValue | undefined,
	selectedLimitRange: RangePickerValue | undefined
) {
	const currentRange =
		args.selectedRangeFromTags?.max === 0 ? selectedLimitRange : selectedRange
	return {
		selectedFilters: args.selectedFiltersFromTags ?? selectedFilters,
		selectedOrder: args.selectedOrderFromTags ?? selectedOrder,
		selectedRange: normalizeRange(currentRange),
		selectedLimitRange: normalizeRange(selectedLimitRange),
	}
}

export function updateRangeLimit(
	currentFilters: UserFilter[],
	originalItems: UseFiltersSlotItem[],
	selected: {
		selectedOrder: string
		selectedRange: RangePickerValue | undefined
		selectedLimitRange: RangePickerValue | undefined
	},
	setters: {
		setSelectedRange: Dispatch<SetStateAction<RangePickerValue | undefined>>
		setSelectedLimitRange: Dispatch<
			SetStateAction<RangePickerValue | undefined>
		>
		setSelectedCount: Dispatch<SetStateAction<number>>
	}
) {
	const { selectedOrder, selectedRange, selectedLimitRange } = selected
	const { setSelectedRange, setSelectedLimitRange, setSelectedCount } = setters
	const filteredItems = filterItems(
		currentFilters,
		originalItems,
		ALL_PRICES,
		ORDER_METHODS[selectedOrder]
	)
	if (filteredItems?.length) {
		const { limit, range } = getLimitAndRange(
			filteredItems,
			selectedRange,
			selectedLimitRange
		)
		setSelectedLimitRange(normalizeRange(limit))
		setSelectedRange(normalizeRange(range))
	}
	if (!filteredItems?.length) {
		eventsObserver.dispatch(EventName.ZERO_RESULTS)
	}
	setSelectedCount(filteredItems?.length ?? 0)
}
